import React, { useState, useEffect, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { ArrowPathIcon, TrashIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline"
import { url, filename } from "aldoo-ra/CMS/url"
import ImagePreview from "aldoo-ra/CMS/preview/image-preview"
import VideoPreview from "aldoo-ra/CMS/preview/video-preview"

export default function ItemSelectionPanel({
  selectedFile,
  selectedFiles = [],
  onReplace,
  onDelete,
  onCopy,
  onSelect,
  onClose,
}) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [mediaResolution, setMediaResolution] = useState(null)
  const videoRef = useRef(null)

  const formatFileSize = (bytes) => {
    if (!bytes) return "0B"
    const sizes = ["B", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
  }

  // Determine file type based on extension
  const getFileExtension = () => {
    if (!selectedFile) return ""
    if (selectedFile.extension) return selectedFile.extension.toLowerCase()
    if (selectedFile.path) {
      const parts = selectedFile.path.split(".")
      return parts.length > 1 ? parts.pop().toLowerCase() : ""
    }
    return ""
  }

  const extension = getFileExtension()

  // Define file type arrays for detection
  const imageTypes = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg"]
  const videoTypes = [".mp4", ".webm", ".mov", ".avi"]

  // Check if file is previewable
  const isImage = extension ? imageTypes.includes(`.${extension}`) : false
  const isVideo = extension ? videoTypes.includes(`.${extension}`) : false
  const hasPreview = isImage || isVideo

  // Get full URL of the file
  const getFileUrl = () => {
    if (!selectedFile) return null
    if (selectedFile.url) return selectedFile.url
    if (selectedFile.path) return url(selectedFile.path)
    return null
  }

  // Get filename for display
  const getFileName = () => {
    if (!selectedFile) return ""
    if (selectedFile.name) return selectedFile.name
    if (selectedFile.path) return filename(selectedFile.path)
    return "Unnamed File"
  }

  // Effect to get media resolution if it's an image or video and resolution is not provided
  useEffect(() => {
    if (!selectedFile || selectedFile.resolution || !getFileUrl()) return

    // Clear previous resolution when file changes
    setMediaResolution(null)

    if (isImage) {
      const img = new Image()
      img.onload = () => {
        setMediaResolution(`${img.naturalWidth}x${img.naturalHeight}`)
      }
      img.src = getFileUrl()
    } else if (isVideo) {
      // Create a hidden video element to get video dimensions
      const video = document.createElement("video")
      video.style.display = "none"

      video.onloadedmetadata = () => {
        setMediaResolution(`${video.videoWidth}x${video.videoHeight}`)
        // Clean up
        document.body.removeChild(video)
      }

      video.onerror = () => {
        console.error("Error loading video metadata")
        // Clean up
        if (document.body.contains(video)) {
          document.body.removeChild(video)
        }
      }

      // Add video to DOM temporarily to load metadata
      document.body.appendChild(video)
      video.src = getFileUrl()

      // Add safety timeout to clean up if metadata doesn't load
      const timeout = setTimeout(() => {
        if (document.body.contains(video)) {
          document.body.removeChild(video)
        }
      }, 5000)

      return () => {
        clearTimeout(timeout)
        if (document.body.contains(video)) {
          document.body.removeChild(video)
        }
      }
    }
  }, [selectedFile, isImage, isVideo])

  if (selectedFiles.length > 1) return (
    <div className="h-14 flex flex-row items-center justify-between px-3 bg-admin_paper border-t border-admin_bordered">
      <Typography variant="admin_txt" className="text-admin_text_secondary">
        {selectedFiles.length} files selected
      </Typography>
      <div className="flex items-center gap-1">
        <button
          title="Delete selected"
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-admin_canvas text-admin_text_secondary hover:text-admin_error"
          onClick={onDelete}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
        {onSelect && (
          <Button variant="admin_primary" className="px-10" onClick={onSelect}>
            Select
          </Button>
        )}
      </div>
    </div>
  )

  if (!selectedFile) return (
    <div className="h-14 flex items-center px-3 bg-admin_paper border-t border-admin_bordered">
      <Typography variant="admin_txt" className="text-admin_text_secondary">
        Select a file
      </Typography>
    </div>
  )

  return (
    <div className="h-14 flex flex-row items-center justify-between px-3 bg-admin_paper border-t border-admin_bordered">
      <div className="flex items-center space-x-2 min-w-0">
        {/* Thumbnail */}
        <div className="w-10 h-10 bg-admin_canvas flex items-center justify-center rounded-md overflow-hidden flex-shrink-0">
          {isImage ? (
            <img
              src={getFileUrl()}
              alt={getFileName()}
              className="w-full h-full object-cover cursor-pointer"
              onClick={() => setIsPreviewOpen(true)}
            />
          ) : isVideo ? (
            <div
              className="w-full h-full flex items-center justify-center bg-admin_primary cursor-pointer"
              onClick={() => setIsPreviewOpen(true)}
            >
              <span className="text-white text-xl">▶</span>
            </div>
          ) : (
            <div className="text-sm font-bold text-admin_text_secondary">
              {extension.toUpperCase()}
            </div>
          )}
        </div>

        {/* Name + meta inline */}
        <div className="flex items-center gap-2 min-w-0">
          <Typography
            variant="admin_txt"
            className={`text-admin_text font-semibold truncate ${hasPreview ? "cursor-pointer hover:text-admin_primary" : ""}`}
            onClick={() => hasPreview && setIsPreviewOpen(true)}
          >
            {getFileName()}
          </Typography>
          <span className="text-admin_text_secondary text-xs flex-shrink-0">
            {selectedFile.formattedSize || formatFileSize(selectedFile.size)}
          </span>
          {(selectedFile.resolution || mediaResolution) && (
            <span className="text-admin_text_secondary text-xs flex-shrink-0">
              {selectedFile.resolution || mediaResolution}
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-row gap-1 items-center flex-shrink-0">
        <button
          title="Replace"
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-admin_canvas text-admin_text_secondary hover:text-admin_text"
          onClick={onReplace}
        >
          <ArrowPathIcon className="w-4 h-4" />
        </button>
        <button
          title="Delete"
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-admin_canvas text-admin_text_secondary hover:text-admin_error"
          onClick={onDelete}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
        <button
          title="Copy URL"
          className="w-8 h-8 flex items-center justify-center rounded hover:bg-admin_canvas text-admin_text_secondary hover:text-admin_text"
          onClick={onCopy}
        >
          <ClipboardDocumentIcon className="w-4 h-4" />
        </button>
        {onSelect && (
          <Button variant="admin_primary" className="px-10" onClick={onSelect}>
            Select
          </Button>
        )}
      </div>

      {/* Preview Modals */}
      {isImage && (
        <ImagePreview
          isOpen={isPreviewOpen}
          imageUrl={getFileUrl()}
          fileName={getFileName()}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
      {isVideo && (
        <VideoPreview
          isOpen={isPreviewOpen}
          videoUrl={getFileUrl()}
          fileName={getFileName()}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </div>
  )
}
